// КОНВЕРТ СЕССИИ ВИЗАРДА — `WizardSession` и его жизненный цикл.
//
// Форма конверта заморожена решением `s864.d1` ЦЕЛИКОМ, а не подмножеством `WizardInput`:
// `continue →` обязан знать сохранённый шаг, а `start over` — быть определённой операцией сброса,
// и то и другое живёт вне `WizardInput`. Любая правка формы после заморозки = подъём
// `schemaVersion`.
//
// ЧТО ЗДЕСЬ, А ЧЕГО НЕТ. Здесь — чистое ядро: создать конверт, спросить «есть ли работа»,
// сбросить по `start over`, восстановить из простого объекта. Ни `localStorage`, ни `history`,
// ни React: запись в хранилище — тикет тихого сохранения (`34a19875cbb881c3b015c19af3999a1c`),
// адрес и история — навигация оболочки. Разделение не косметическое: ядро обязано быть
// проверяемым без браузера, иначе приёмка пунктов 1 и 5 упрётся в стенд, которого у неё нет.
//
// `nowY` — ОДИН ЗАХВАТ. Часы читаются ровно здесь и больше нигде: адаптер
// (`src/wizard/toWizardInput.js`, токен «never read from the clock in here») часы не трогает,
// а `READING_YEAR` в контракте — фолбэк рендера, не источник. «Заморозка на сабмите» из матрицы
// трассировки — момент, когда значение перестаёт быть изменяемым, а не второй захват.
// Легальное перезахватывание одно: `start over` (`s866.d1`) — новый проход, новый якорь.
//
// ⭐ `nowY` ХРАНИТ МЕСЯЦ (`s898.d1`, слово Кирилла 23.09). До этого поле было ЦЕЛЫМ ГОДОМ, и
// конец текущего этапа считался до ЯНВАРЯ: старт `03/2023` печатался `2y 10m` вместо календарных
// `3y 6m` — недосчёт до одиннадцати месяцев у каждого человека, доезжающий в паспорт (замер CC,
// cc-132). Старты этапов месяц несли всегда; «сейчас» было единственной величиной, округлённой
// до года.
//
// ФОРМА — ДЕСЯТИЧНЫЙ ГОД, а не пара полей: ровно та, которую уже отдаёт `toDecimalYear`
// (`MM/YYYY` → `Y + (M−1)/12`) и которую ждёт `endY` последнего этапа. Поэтому потребителей
// правка не тронула ни одного: адаптер клал `nowY` в `endY` как есть и кладёт как есть.
// Цена формы названа: значение обязано НАЗЫВАТЬ МЕСЯЦ, то есть быть кратно `1/12`. Число вроде
// `2026.3` не «почти март», а мусор, и `captureNowY` его бракует — так же громко, как раньше
// бракова́л нецелое.
//
// ⚠️ КРАЕВОЙ СЛУЧАЙ, ОСТАВЛЕННЫЙ НАМЕРЕННО (решение Кирилла, cc-122). Сессия, начатая 31 декабря
// и отправленная 2 января, унесёт год старта. Перезахвата на сабмите не будет: детерминизм
// восстановления дороже редкого документа с годом, отстающим на один.

/**
 * @typedef {Object} WizardInputRows
 * @property {Array} stages   этапы (имя, даты) — `StageMeta[]`
 * @property {Array} sliders  семь слоёв на этап — `SlidersRow[]`
 * @property {Array} text     свободный текст на этап — `TextRow[]`
 * @property {Array} buttons  группы кнопок BG1–BG4 — `ButtonRow[]`
 * @property {Array} cust     свободная строка BG3 на этап — `string[]` (`s516.d1`, схема 3)
 */

/**
 * @typedef {Object} WizardSession
 * @property {number} schemaVersion  версия конверта; ключ `localStorage` версионирован ею
 * @property {string} sessionId      аноним-id, крючок под resume-токен Track D
 * @property {number} nowY           «сейчас» десятичным годом с месяцем (`s898.d1`)
 * @property {string} step           адрес шага/подшага (`s1`, `s3/2/b`) — хром §4
 * @property {WizardInputRows} input четыре ряда `WizardInput`, на `begin` пустые
 * @property {StageDraft|null} stageDraft  незакрытый черновик шага 2 (`s895.d2`)
 * @property {string[]} revealsPlayed  адресоподобные токены событий, сыгранных ОДИН РАЗ ЗА СЕССИЮ
 *   (`s864.d1` reveal хрома §4; `s903.d2` расширил формулировку — сюда же `'s3/wall'`, флаг стены
 *   шага 3: скоуп сессии, сбрасывает только `start over`, перезагрузку вкладки переживает).
 *   Отдельного поля под флаг стены нет и не заводится — форма конверта не меняется.
 * @property {{key: string, system2: object|null}|null} quotes  результат судьи цитат (схема 4, `s987.d3`):
 *   `key` — отпечаток входа судьи (`quotes.js` `quotesKey`), `system2` — выход `selectQuotes`. `null` — не
 *   звали. Читается только через `quotesOf`, который сверяет ключ с текущим вводом.
 */

/**
 * @typedef {Object} StageDraft  черновик ряда шага 2 — ОТДЕЛЬНЫЙ СЛОТ, не элемент `input.stages`
 * @property {string} name        набранное имя; пустая строка = ещё не назван
 * @property {string|null} start  `MM/YYYY` или `null`, пока месяц не выбран
 * @property {number|null} editing  индекс правимого ряда в `input.stages`, `null` = новый этап
 */

import { STEP_COUNT, stepNumber } from './address.js';
// Форма рядов шага 3 сверяется с ДВУМЯ существующими словарями, а не со своими копиями: семь
// слоёв — `LAYERS` контракта, четыре группы — `GROUP_IDS` адаптера. Вторая копия любого из них
// разъехалась бы с первой молча, и конверт начал бы принимать ряды, которых адаптер не понимает.
import { LAYERS } from '../contracts.js';
import { GROUP_IDS } from './toWizardInput.js';

/**
 * Текущая версия формы конверта. Меняется ТОЛЬКО вместе с правкой состава полей (`s864.d1`).
 *
 * `1 → 2` (B4-2, 23.09) — один подъём под ТРИ изменения формы, потому что второго подъёма за
 * тот же блок платить незачем: месяц в `nowY` (`s898.d1`), слот черновика `stageDraft`
 * (`s895.d2`) и ссылка `stageDraft.editing` на правимый ряд (`s898.d2`, чтобы поведение `✎` в
 * B4-3 не поднимало версию второй раз).
 *
 * ⚠️ ЦЕНА ПОДЪЁМА НАЗВАНА ВСЛУХ. Это первый раз, когда ветка `REJECT.VERSION` срабатывает
 * по-настоящему: каждый сохранённый ключ в живом браузере будет отвергнут и стёрт молча
 * (`s893.d1` — человеку не печатаем ничего), то есть чья-то незаконченная сессия обнулится.
 * До платного трафика это бесплатно; после — миграция, а не подъём.
 * ⛔ Правило держит машина, а не этот абзац: `src/wizard/envelopeVersion.test.js` (G-T6) —
 * накопительный набор образцов конверта (`__fixtures__/envelope-samples/`), каждый обязан
 * приниматься текущей версией с целыми ответами. Подъём без миграции — красный прогон.
 *
 * `2 → 3` (B5-4, 28.09) — ОДНО новое поле, `input.cust`: свободная строка BG3 по этапу. Дом у неё
 * мог быть только здесь. Дека держит сентинел `__cust` в ряду кнопок, а набранный текст — рядом
 * (`S.cust[i]` борда), и адаптер их сводит (`toWizardInput`, `s510.d4a`); класть текст внутрь
 * ряда кнопок значило бы менять форму, которую адаптер уже читает, — то есть платить той же
 * версией за большее. Второго изменения формы в этом подъёме нет: флаг стены живёт записью
 * `'s3/wall'` в `revealsPlayed` (`s903.d2`) и поля себе не заводит.
 */
export const SCHEMA_VERSION = 4;

/**
 * `3 → 4` (строка 73, 15.10) — ОДНО новое поле верхнего уровня, `quotes`: результат судьи цитат, позванного
 * на выходе с шага 3 (`s987.d3`). ⭐ ЭТО ПЕРВЫЙ ПОДЪЁМ С МИГРАЦИЕЙ, а не со стиранием: визард уже на живом
 * адресе, и конверт версии 3 — чья-то работа. Миграция тривиальна и без потерь: у конверта 3 судью не звали,
 * `quotes: null`, остальное как было (`MIGRATE`). Образец `v3.json` обязан проходить (G-T6).
 */
const MIGRATE = {
  3: (v) => ({ ...v, schemaVersion: 4, quotes: null }),
};

/** Адрес первого шага. Шагов семь (`s447.d3`), счётчик в хроме — `NN / 07`. */
export const FIRST_STEP = 's1';

/**
 * Ряды `WizardInput`. Порядок — как в `s864.d1`, плюс `cust` пятым (схема 3, B5-4).
 *
 * ⚠️ `cust` ВХОДИТ И В ПРЕДИКАТ «РАБОТА ЕСТЬ», и это не автоматизм списка. Свободная строка —
 * единственный ответ панели B, который человек НАПИСАЛ, а не выбрал; конверт, где она есть, а
 * всё остальное пусто, произвести нельзя (чип поднимается раньше текста), но если он приедет из
 * чужой вкладки, назвать его «работы нет» и предложить `begin` значило бы стереть набранное.
 */
const INPUT_ROWS = ['stages', 'sliders', 'text', 'buttons', 'cust'];

/** Пустые ряды. Каждый вызов — свежие массивы: общий литерал утёк бы между сессиями. */
function emptyInput() {
  return { stages: [], sliders: [], text: [], buttons: [], cust: [] };
}

/**
 * Называет ли число месяц: десятичный год кратен `1/12` (`s898.d1`).
 *
 * ДОПУСК НУЖЕН НЕ РАДИ СВОИХ ЗНАЧЕНИЙ. Замер (1900–2100, все 12 месяцев): `(Y + m/12) * 12`
 * в double выходит ТОЧНЫМ целым во всех 2412 случаях — то есть то, что производит `captureNowY`,
 * прошло бы и строгое сравнение. Допуск стоит под ЧУЖИЕ кодировки того же числа: сентябрь 2026,
 * записанный как `2026.66666`, даёт `24319.99992`, и строгая проверка назвала бы его мусором,
 * хотя месяц он называет однозначно. Ширина окна выбрана так, чтобы в него не попадало
 * соседнее деление: шаг месяца — `1/12`, то есть в тысячи раз больше `1e-6`.
 */
export function isMonthYear(v) {
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return false;
  return Math.abs(v * 12 - Math.round(v * 12)) < 1e-6;
}

/**
 * «Сейчас» десятичным годом С МЕСЯЦЕМ (`s898.d1`). Единственное чтение часов во всём визарде.
 *
 * Вынесено отдельной функцией, чтобы тест подменял момент явно, а не подкручивал системное время.
 * Часы дают `getMonth()` с нуля — это и есть `(M−1)` формулы `toDecimalYear`, поэтому январь
 * текущего года равен целому числу, и старый вид подмены (`clock: () => 2026`) остаётся законным
 * значением: он теперь означает «январь 2026», а не «год 2026 без месяца».
 */
export function captureNowY(clock = () => {
  const d = new Date();
  return d.getFullYear() + d.getMonth() / 12;
}) {
  const y = clock();
  if (!isMonthYear(y)) {
    throw new Error(`nowY обязан называть месяц (десятичный год кратно 1/12), получено: ${y}`);
  }
  return y;
}

/**
 * Аноним-id сессии. `crypto.randomUUID` есть в браузерах, под которые собираемся, и в node 18+;
 * фолбэк оставлен не для экзотики, а для jsdom без `crypto` — молчаливый `undefined` в этом
 * поле осиротил бы resume-токен Track D незаметно.
 */
function newSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Создать конверт новой сессии. Владелец жизненного цикла — оболочка визарда; хэндлер `begin`
 * шага 1 только вызывает это и формы стора не знает (`s864.d1`).
 *
 * @param {{clock?: () => number, sessionId?: string}} [opts] подмены для тестов
 * @returns {WizardSession}
 */
export function createSession(opts = {}) {
  return {
    schemaVersion: SCHEMA_VERSION,
    sessionId: opts.sessionId ?? newSessionId(),
    nowY: captureNowY(opts.clock),
    step: FIRST_STEP,
    input: emptyInput(),
    stageDraft: null,
    revealsPlayed: [],
    quotes: null,
  };
}

/** Тронут ли хоть один ряд ввода. Любой непустой ряд считается работой (`s866.d1`). */
export function inputNonEmpty(input) {
  if (!input || typeof input !== 'object') return false;
  return INPUT_ROWS.some((row) => Array.isArray(input[row]) && input[row].length > 0);
}

/** Несёт ли черновик хоть что-то набранное. Пустой слот от `null` не отличается по смыслу. */
export function draftNonEmpty(draft) {
  if (!draft || typeof draft !== 'object') return false;
  return !!(draft.name || draft.start) || Number.isInteger(draft.editing);
}

/**
 * Предикат «работа есть» — что показывает шаг 1: `continue →` или `begin` (`s866.d1`).
 *
 * `hasWork = step > 1 || непустой ввод || непустой черновик`. Читается ТОЛЬКО из сохранённого
 * конверта, не из памяти.
 *
 * ⚠️ ТРЕТЬЕ СЛАГАЕМОЕ ПРИШЛО С `s895.d2`, И ЭТО СОХРАНЕНИЕ ПОВЕДЕНИЯ, А НЕ НОВОЕ ПРАВИЛО.
 * `s866.d1` обещает, что черновик в пол-этапа считается работой; пока черновик жил рядом в
 * `rows`, это давал `inputNonEmpty` сам собой. Решение вынесло его в отдельный слот — и обещание
 * держалось бы ничем, если бы предикат остался прежним: человек, набравший имя и закрывший
 * вкладку, увидел бы `begin` вместо `continue` и своё имя не нашёл.
 */
export function hasWork(session) {
  if (!session || typeof session !== 'object') return false;
  return stepNumber(session.step) > 1
    || inputNonEmpty(session.input)
    || draftNonEmpty(session.stageDraft);
}

/**
 * Номер шага из адреса: `s3/2/b` → 3. Живёт в грамматике адреса (`./address.js`) — ядро сессии
 * только пользуется им и пере-экспортирует, чтобы у читателя конверта не было второй реализации.
 */
export { stepNumber };

/**
 * `start over` (`s866.d1`): сбрасывает `input` + `step` + `revealsPlayed`, СОХРАНЯЕТ `sessionId`
 * и `schemaVersion`, `nowY` захватывается заново.
 *
 * Ключ целиком не стирается: `sessionId` — аноним-идентичность под серверный resume-токен
 * Track D, стереть его значит осиротить токен. Возвращается НОВЫЙ объект — прежний конверт не
 * мутируется, чтобы держатель стейта видел смену ссылки.
 */
export function startOver(session, opts = {}) {
  if (!session || typeof session !== 'object') {
    throw new Error('start over нечего сбрасывать: конверта нет');
  }
  return {
    schemaVersion: session.schemaVersion,
    sessionId: session.sessionId,
    nowY: captureNowY(opts.clock),
    step: FIRST_STEP,
    input: emptyInput(),
    stageDraft: null,
    revealsPlayed: [],
    quotes: null,
  };
}

/** Причины, по которым сохранённый конверт не принимается. Различимы под будущую миграцию. */
export const REJECT = {
  /** Значения нет вовсе. */
  ABSENT: 'absent',
  /** Не объект / не разобрался / полей не хватает. */
  CORRUPT: 'corrupt',
  /** Разобрался, но версия не наша — `s889.d4`. */
  VERSION: 'version',
};

/**
 * Принять разобранный объект как конверт сессии.
 *
 * Возвращает `{session}` либо `{reason}` — ветка отказа НАЗВАНА, а не выведена вызывающим.
 * Обе причины сейчас ведут к чистому старту, но различаются здесь, потому что миграция между
 * версиями будет ветвиться именно тут (`s889.d4`).
 *
 * Разбор строки (и `SyntaxError` на битом JSON) — не здесь: это дело того, кто владеет ключом.
 */
export function acceptSession(value) {
  if (value === null || value === undefined) return { reason: REJECT.ABSENT };
  if (typeof value !== 'object' || Array.isArray(value)) return { reason: REJECT.CORRUPT };
  // Миграция — цепочкой по одной версии, ДО проверки версии: старый конверт сначала становится текущим.
  while (value && typeof value.schemaVersion === 'number' && MIGRATE[value.schemaVersion]) {
    value = MIGRATE[value.schemaVersion](value);
  }

  if (value.schemaVersion !== SCHEMA_VERSION) {
    // Версия читается ПЕРВОЙ: чужой конверт имеет право на чужую форму, и придираться к его
    // полям по нашей мерке — значит назвать причиной `corrupt` то, что на самом деле `version`.
    return { reason: typeof value.schemaVersion === 'number' ? REJECT.VERSION : REJECT.CORRUPT };
  }

  if (typeof value.sessionId !== 'string' || !value.sessionId) return { reason: REJECT.CORRUPT };
  // `s898.d1`: месяц обязателен. Старое «целый год» тоже проходит — январь это законный месяц,
  // — но конверты прошлой версии сюда не доходят вовсе: их отсекает проверка версии выше.
  if (!isMonthYear(value.nowY)) return { reason: REJECT.CORRUPT };
  // ⭐ ГРАНИЦА ЧИТАЕТСЯ С ОБЕИХ СТОРОН (`B5-F2`, находка F-2 прогона `B5-QA`). Верхней тут не
  // было, и асимметрия стоила белого экрана: `parseAddress`, `formatAddress`, `stepCounter` и
  // `footerFor` все меряют `1..STEP_COUNT`, а единственные ворота, через которые чужая строка
  // попадает в конверт, — нет. Принятый `s99` падал позже и в худшем месте: `stepCounter` бросает
  // в ТЕЛЕ РЕНДЕРА, где ловить некому. Отказ здесь — тот же чистый старт, что у битого конверта.
  const step = stepNumber(value.step);
  if (step < 1 || step > STEP_COUNT) return { reason: REJECT.CORRUPT };
  if (!Array.isArray(value.revealsPlayed)) return { reason: REJECT.CORRUPT };
  if (!value.input || typeof value.input !== 'object') return { reason: REJECT.CORRUPT };
  if (INPUT_ROWS.some((row) => !Array.isArray(value.input[row]))) return { reason: REJECT.CORRUPT };
  if (!step3RowsOK(value.input)) return { reason: REJECT.CORRUPT };

  const draft = acceptStageDraft(value.stageDraft);
  if (draft === CORRUPT_DRAFT) return { reason: REJECT.CORRUPT };

  return {
    session: {
      schemaVersion: value.schemaVersion,
      sessionId: value.sessionId,
      nowY: value.nowY,
      step: value.step,
      input: {
        stages: value.input.stages,
        sliders: value.input.sliders,
        text: value.input.text,
        buttons: value.input.buttons,
        cust: value.input.cust,
      },
      stageDraft: draft,
      revealsPlayed: value.revealsPlayed,
      quotes: acceptQuotes(value.quotes),
    },
  };
}

/**
 * Слот цитат (схема 4). Кривой слот НЕ бракует конверт: это кэш вызова, а не работа человека, — стирать
 * за него ответы было бы ценой не по вине. Кривой → `null`, судья позовётся заново.
 */
function acceptQuotes(q) {
  if (!q || typeof q !== 'object' || Array.isArray(q)) return null;
  if (typeof q.key !== 'string' || !q.key) return null;
  if (q.system2 !== null && (typeof q.system2 !== 'object' || Array.isArray(q.system2))) return null;
  return { key: q.key, system2: q.system2 };
}

/**
 * ФОРМА РЯДОВ ШАГА 3 (B5-1). До этого места конверт проверял у `sliders`/`text`/`buttons` ровно
 * одно — «это массив», — и кривой ряд из чужой вкладки доезжал до адаптера. Адаптер на нём не
 * падает внятно: он бросает про рассинхрон длин или молча кладёт `undefined` в паспорт.
 *
 * ⚠️ РЯДЫ ЗДЕСЬ — ЭКРАННЫЕ, А НЕ КОНТРАКТНЫЕ, и путать их нельзя. Конверт хранит то, что собирает
 * дека (`S.vals` / `S.text` / `S.grp`), а в форму `WizardInput` их переводит `toWizardInput`.
 * Поэтому `sliders` — МАССИВ из семи значений по порядку `LAYERS`, а не объект с ключами слоёв:
 * объект с ключами — это уже выход адаптера. Сверено с фабрикой фикстур (`step3State`), которая
 * этот же конверт и собирает.
 *
 * ⛔ ДЛИНЫ РЯДОВ МЕЖДУ СОБОЙ ЗДЕСЬ НЕ СВЕРЯЮТСЯ (`s972.d1` п. 3, B5-F1). Конверт отвечает на вопрос
 * «эти ряды вообще нашей формы», и брак конверта стирает всю работу человека — поэтому:
 *   · КОРОЧЕ списка этапов — законно: после шага 2 конверт несёт `sliders: []`, панели B/C пишутся
 *     только при касании (ленивая запись деки);
 *   · ДЛИННЕЕ — невозможно по построению с B5-F1: правки списка на шаге 2 двигают ответы вместе с
 *     этапом (`steps/stageAnswers.js`). Остаётся только правка руками, и её ловит не конверт, а
 *     допуск B10-1 (`stepAdmission.js`, шаги 4+) и шов сабмита B10-4 (`submitChain.js`, слот 4).
 *
 * ⛔ ДИАПАЗОН ЗНАЧЕНИЙ (∓3) ТОЖЕ НЕ ПРОВЕРЯЕТСЯ. Это предмет `validateWizardInput`, у него своя
 * формулировка и свой момент; дублировать её здесь значило бы завести второе место, где живёт
 * граница ползунка. Конверт смотрит на ТИП: конечное число или `null` (untouched легален,
 * `s443.d5`).
 */
function step3RowsOK(input) {
  const slidersOK = input.sliders.every((row) => (
    Array.isArray(row)
      && row.length === LAYERS.length
      && row.every((v) => v === null || (typeof v === 'number' && Number.isFinite(v)))
  ));
  if (!slidersOK) return false;

  const textOK = input.text.every((row) => (
    row && typeof row === 'object' && !Array.isArray(row)
      && ['T1', 'T2', 'T3'].every((f) => typeof row[f] === 'string')
  ));
  if (!textOK) return false;

  const buttonsOK = input.buttons.every((row) => (
    row && typeof row === 'object' && !Array.isArray(row)
      && GROUP_IDS.every((id) => Array.isArray(row[id]) && row[id].every((o) => typeof o === 'string'))
  ));
  if (!buttonsOK) return false;

  // ⛔ ДЛИНА СТРОКИ (`LIVING_FOR_MAX`) ЗДЕСЬ НЕ ПРОВЕРЯЕТСЯ — по той же причине, по которой не
  // проверяется диапазон ползунка: это правило ВВОДА, у него свой момент и свой дом (`maxlength`
  // поля, `s510.d3`). Конверт отвечает на вопрос «это наша форма», а не «это допустимое
  // значение»; браковать восстановленную сессию за длинную строку значило бы стереть работу.
  return input.cust.every((v) => typeof v === 'string');
}

/** Метка «черновик не разобрался». Отдельное значение, потому что `null` здесь легален. */
const CORRUPT_DRAFT = Symbol('corrupt draft');

/**
 * Принять слот черновика (`s895.d2`, `s898.d2`).
 *
 * Отсутствие слота и `null` — одно и то же: черновика нет. Требовать литеральный `null` значило
 * бы браковать конверт за форму записи, ничего при этом не гарантируя: пустой слот и отсутствие
 * слота неразличимы по смыслу.
 *
 * Все три поля обязательны и проверяются по типу: `name` — строка (пустая = ещё не назван),
 * `start` — `MM/YYYY`-строка или `null`, `editing` — неотрицательный целый индекс ряда или
 * `null`. Недостающее поле — тоже `corrupt`, а не «подставим значение по умолчанию»: слот
 * пишется целиком одним местом, и половинчатая запись означает, что его писал кто-то другой.
 */
function acceptStageDraft(value) {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'object' || Array.isArray(value)) return CORRUPT_DRAFT;

  const { name, start, editing } = value;
  if (typeof name !== 'string') return CORRUPT_DRAFT;
  if (start !== null && typeof start !== 'string') return CORRUPT_DRAFT;
  if (editing !== null && !(Number.isInteger(editing) && editing >= 0)) return CORRUPT_DRAFT;

  return { name, start, editing };
}
